<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import FormField from '@/components/FormField.vue'
import Snackbar from '@/components/Snackbar.vue'
import apiInstance from '@/utils/api'
import { BaseButton, BaseInput, BaseModalClose } from '@/components/base'

const { t } = useI18n()
const authStore = useAuthStore()

interface Props {
  isOpen: boolean
}

interface Emits {
  close: []
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const isLoading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const fieldErrors = ref<Record<string, string[]>>({})

// Initialize form with current user data when modal opens
const initializeForm = () => {
  if (authStore.user) {
    firstName.value = authStore.user.firstName || ''
    lastName.value = authStore.user.lastName || ''
    email.value = authStore.user.email || ''
  }
}

onMounted(() => {
  initializeForm()
})

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) initializeForm()
  }
)

watch(
  () => authStore.user,
  () => {
    initializeForm()
  },
  { deep: true }
)

const resetForm = () => {
  initializeForm()
  successMessage.value = ''
  errorMessage.value = ''
  fieldErrors.value = {}
}

const handleCancel = () => {
  resetForm()
  emit('close')
}

const validateForm = (): boolean => {
  const errors: Record<string, string[]> = {}

  if (!firstName.value.trim()) errors.first_name = [t('auth.validation.firstNameRequired')]
  if (!lastName.value.trim()) errors.last_name = [t('auth.validation.lastNameRequired')]

  fieldErrors.value = errors
  return Object.keys(errors).length === 0
}

const handleSave = async () => {
  successMessage.value = ''
  errorMessage.value = ''
  fieldErrors.value = {}

  if (!validateForm()) {
    errorMessage.value = t('profileUpdate.validationError')
    return
  }

  isLoading.value = true

  try {
    const response = await apiInstance.put('/user/profile', {
      first_name: firstName.value,
      last_name: lastName.value,
    })

    if (response.data.error === false) {
      successMessage.value = response.data.message || t('profileUpdate.success')

      if (response.data.data && authStore.user) {
        const data = response.data.data
        authStore.user = {
          ...authStore.user,
          firstName: data.first_name,
          lastName: data.last_name,
          name: `${data.first_name} ${data.last_name}`,
        }
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        resetForm()
        emit('close')
      }, 2000)
    } else {
      if (response.data.errors && typeof response.data.errors === 'object') {
        fieldErrors.value = response.data.errors
        errorMessage.value = response.data.message || t('profileUpdate.validationError')
      } else {
        errorMessage.value = response.data.message || t('profileUpdate.error')
      }
    }
  } catch (error: any) {
    console.error('Error updating profile:', error)

    if (error.response?.status === 422 && error.response?.data?.errors) {
      fieldErrors.value = error.response.data.errors
      errorMessage.value = error.response.data.message || t('profileUpdate.validationError')
    } else if (error.response?.data?.message) {
      errorMessage.value = error.response.data.message
    } else {
      errorMessage.value = error.message || t('profileUpdate.error')
    }
  } finally {
    isLoading.value = false
  }
}

const getFieldError = (fieldName: string): string => {
  const errors = fieldErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : ''
}

</script>

<template>
  <!-- Kept outside the modal so the success toast survives the modal closing after a save -->
  <Snackbar :success-message="successMessage || undefined" :error-message="errorMessage || undefined" />

  <!-- Modal Backdrop -->
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
  >
    <!-- Modal Content -->
    <div class="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" @click.stop>
      <!-- Modal Header -->
      <div class="sticky top-0 bg-white border-b border-gray-200 flex items-center justify-between z-10 px-6 py-4">
        <h2 class="text-gray-900 text-xl font-bold">{{ t('profileUpdate.title') }}</h2>
        <BaseModalClose @click="handleCancel" :disabled="isLoading" />
      </div>

      <!-- Modal Body -->
      <div class="px-6 py-4">
        <form @submit.prevent="handleSave" class="space-y-5">
          <!-- Email stays read-only — it is the login credential. -->
          <FormField
            :label="t('profileUpdate.email')"
            :helperText="t('profileUpdate.emailCannotChange')"
          >
            <BaseInput :value="email" type="email" disabled class="bg-gray-50 cursor-not-allowed" />
          </FormField>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField :label="t('profileUpdate.firstName')" required :error="getFieldError('first_name')">
              <BaseInput
                v-model="firstName"
                type="text"
                :disabled="isLoading"
                :placeholder="t('profileUpdate.firstNamePlaceholder')"
              />
            </FormField>

            <FormField :label="t('profileUpdate.lastName')" required :error="getFieldError('last_name')">
              <BaseInput
                v-model="lastName"
                type="text"
                :disabled="isLoading"
                :placeholder="t('profileUpdate.lastNamePlaceholder')"
              />
            </FormField>
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
        <BaseButton
          @click="handleCancel"
          :disabled="isLoading"
          variant="secondary"
        >
          {{ t('common.cancel') }}
        </BaseButton>
        <BaseButton
          @click="handleSave"
          :disabled="isLoading"
        >
          <span v-if="!isLoading">{{ t('common.save') }}</span>
          <span v-else class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ t('common.saving') }}
          </span>
        </BaseButton>
      </div>
    </div>
  </div>
</template>
